import React from 'react';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from './protectedRoute';

// Layouts
import AdminLayout from '@/layout/admin/AdminLayout';
import AuthLayout from '@/layout/auth/AuthLayout';
import MainLayout from '@/layout/front/MainLayout';

// Common pages
import NotFoundPage from '@/views/NotFoundPage';
import UnauthorizedPage from '@/views/UnauthorizedPage';

// Auth pages
import ForgotPasswordPage from '@/views/auth/ForgotPasswordPage';
import LoginPage from '@/views/auth/LoginPage';
import CustomerLoginPage from '@/views/auth/CustomerLoginPage';
import ResetPasswordPage from '@/views/auth/ResetPasswordPage';
import VerifyEmailPage from '@/views/auth/VerifyEmailPage';
import ActivatePage from '@/views/auth/ActivatePage';
import LicenseStatusPage from '@/views/auth/LicenseStatusPage';
import SetupPage from '@/views/auth/SetupPage';

// Front pages
import TablePosPage from '@/views/front/pos/index';
import CheckoutPage from '@/views/front/checkout/CheckoutPage';
import OrderDetailsPage from '@/views/front/order-detail/OrderDetailsPage';
import PaymentCheckPage from '@/views/front/payment-check/index';
import FrontCmsPage from '@/views/front/cms-page/index';
import WelcomeSSOPage from '@/views/auth/WelcomeSSOPage';

// Admin Dashboard & modules
import Dashboard from '@/views/admin/dashboard/index';
import DiningTablesPage from '@/views/admin/products/dining-tables/index';
import DiningTableViewPage from '@/views/admin/products/dining-tables/view/index';
import ItemsPage from '@/views/admin/products/items/index';
import ItemViewPage from '@/views/admin/products/items/view/index';
import PosPage from '@/views/admin/pos/pos/index';
import PosOrdersPage from '@/views/admin/pos/pos-orders/index';
import TableOrdersPage from '@/views/admin/pos/table-orders/index';
import OffersPage from '@/views/admin/promo/offers/index';
import OfferViewPage from '@/views/admin/promo/offers/view/index';
import AdministratorsPage from '@/views/admin/users/admin/index';
import AdminViewPage from '@/views/admin/users/admin/view/index';
import CustomersPage from '@/views/admin/users/customers/index';
import CustomerViewPage from '@/views/admin/users/customers/view';
import EmployeesPage from '@/views/admin/users/employees/index';
import EmployeeViewPage from '@/views/admin/users/employees/view/index';
import TransactionsPage from '@/views/admin/accounts/transactions/index';
import CreditBalanceReportsPage from '@/views/admin/reports/credit-balance/index';
import ItemsReportsPage from '@/views/admin/reports/items/index';
import SalesReportsPage from '@/views/admin/reports/sales/index';
import BranchesPage from '../views/admin/settings/branches';

// POS / Table Orders Details
import PosOrderDetailsPage from '@/views/admin/pos/pos-orders/details/index';
import TableOrdersDetailsPage from '@/views/admin/pos/table-orders/details/index';

// Profile
import EditProfilePage from '@/views/admin/profile/index';
import ChangePasswordPage from '@/views/admin/profile/changePassword';

// Admin Settings
import AdminSettingsLayout from '../layout/admin/AdminSettingsLayout';
import CompanyPage from '../views/admin/settings/company/index';
import SitePage from '../views/admin/settings/site/index';
import MailPage from '../views/admin/settings/mail/index';
import AnalyticsPage from '../views/admin/settings/analytics/index';
import CurrenciesPage from '../views/admin/settings/currencies/index';
import ItemAttributesPage from '../views/admin/settings/item-attributes/index';
import ItemCategoriesPage from '../views/admin/settings/item-categories/index';
import NotificationPage from '../views/admin/settings/notification/index';
import NotificationAlertPage from '../views/admin/settings/notification-alert/index';
import OTPPage from '../views/admin/settings/otp/index';
import ShimbirrAuthSettingsPage from '../views/admin/settings/shimbirr-auth/index';
import PaymentGatewayPage from '../views/admin/settings/payment-gateway/index';
import RolePermissionPage from '../views/admin/settings/role-permissions/index';
import SmsGatewayPage from '../views/admin/settings/sms-gateway/index';
import TaxPage from '../views/admin/settings/taxes/index';
import ThemePage from '../views/admin/settings/theme/index';
import LicenseSettingsPage from '../views/admin/settings/license';
import CMSPage from '../views/admin/settings/cms-pages/index';
import RoleUpdatePage from '../views/admin/settings/role-permissions/role/RoleUpdatePage';

// HR: Attendance & Leave
import AttendancePage from '../views/admin/users/attendance/index';
import LeaveRequestsPage from '../views/admin/users/leaves/index';
// Inventory
import InventoryPage from '../views/admin/inventory/index';

// Customer area
import CustomerLayout from '@/layout/customer/CustomerLayout';
import CustomerDashboard from '../views/admin/users/customers/dashboard/index';
import CustomerProfilePage from '@/views/admin/users/customers/profile/CustomerProfilePage';
import CustomerChangePasswordPage from '@/views/admin/users/customers/profile/ChangePasswordPage';
import CustomerCreditPage from '../views/admin/users/customers/dashboard/pages/CustomerCreditPage';
import CustomerUnpaidCreditPage from '../views/admin/users/customers/dashboard/pages/CustomerUnpaidCreditPage';
import CustomerOrdersPage from '../views/admin/users/customers/dashboard/pages/CustomerOrdersPage';
import CustomerPayCreditPage from '../views/admin/users/customers/pay-credit/CustomerPayCreditPage';

// Chef & Waiter
import ChefsPage from '@/views/admin/users/chefs/index';
import ChefViewPage from '@/views/admin/users/chefs/view';
import WaitersPage from '@/views/admin/users/waiters/index';
import WaiterViewPage from '@/views/admin/users/waiters/view';

// Kitchen / Waiter boards
import WaiterBoard from '@/views/admin/waiter/WaiterBoard';
import KitchenBoard from '@/views/admin/kitchen/KitchenBoard';
import KitchenScreen from '@/views/admin/kitchenScreen/KitchenScreen';
import HRMSettingsPage from '../views/admin/settings/hrm/index';
const routes = (isAuthenticated, isSuperAdmin, permissions) => [
  // Redirect root
// routes.js — REPLACE the two top-level "/" entries with this single block
{
  path: '/',
  element: !isAuthenticated ? <AuthLayout /> : <Navigate to="/admin/dashboard" />,
  children: [
    // default landing for unauthenticated users
    { index: true, element: <Navigate to="sign-in" replace /> },

    // auth pages (relative paths!)
    { path: 'sign-in', element: <LoginPage /> },
    { path: 'sign-in-customer', element: <CustomerLoginPage /> },
    { path: 'forget-password', element: <ForgotPasswordPage /> },
    { path: 'forget-password/verify', element: <VerifyEmailPage /> },
    { path: 'reset-password', element: <ResetPasswordPage /> },
    { path: 'license-status', element: <LicenseStatusPage /> },

    { path: '*', element: <NotFoundPage /> },
  ],
},
{
  path: '/welcome',
  element: <WelcomeSSOPage />,
},
{
  path: '/activate',
  element: <ActivatePage />,
},
{
  path: '/setup',
  element: <SetupPage />,
},

  // Front (public) layout
  {
    path: '/menu',
    element: <MainLayout />,
    children: [
      { path: '/menu/table/:slug', element: <TablePosPage /> },
      { path: '/menu/checkout/:slug', element: <CheckoutPage /> },
      { path: '/menu/table-order/:slug/:id', element: <OrderDetailsPage /> },
      { path: '/menu/page/:slug', element: <FrontCmsPage /> },
      { path: '/menu/payment/:slug/:id', element: <PaymentCheckPage /> },
    ],
  },

  // Admin area
  {
    path: '/admin',
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated} permissions={permissions}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/admin/dashboard',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_dashboard"
          >
            <Dashboard />
          </ProtectedRoute>
        ),
      },

      /* ===== Items ===== */
      {
        path: '/admin/items',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_items"
          >
            <ItemsPage pageTitle="Items" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/item/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_items"
          >
            <ItemViewPage pageTitle="Item / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Dining tables ===== */
      {
        path: '/admin/dining-tables',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_dining_tables"
          >
            <DiningTablesPage pageTitle="Dining Tables" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/dining-tables/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_dining_tables"
          >
            <DiningTableViewPage pageTitle="Dining Tables" />
          </ProtectedRoute>
        ),
      },

      /* ===== POS & Orders ===== */
      {
        path: '/admin/pos',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_pos"
          >
            <PosPage pageTitle="POS" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/pos-orders',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_pos_orders"
          >
            <PosOrdersPage pageTitle="POS Orders" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/pos-orders/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_pos_orders"
          >
            <PosOrderDetailsPage pageTitle="POS Orders / View" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/table-orders',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_table_orders"
          >
            <TableOrdersPage pageTitle="Table Orders" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/table-orders/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_table_orders"
          >
            <TableOrdersDetailsPage pageTitle="Table Orders / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Offers ===== */
      {
        path: '/admin/offers',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_offers"
          >
            <OffersPage pageTitle="Offers" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/offers/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_offers"
          >
            <OfferViewPage pageTitle="Offers / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Admin users ===== */
      {
        path: '/admin/administrators',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_admin"
          >
            <AdministratorsPage pageTitle="Administrators" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/administrators/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_admin"
          >
            <AdminViewPage pageTitle="Administrators / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Customers ===== */
      {
        path: '/admin/customers',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_customers"
          >
            <CustomersPage pageTitle="Customers" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/customers/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_customers"
          >
            <CustomerViewPage pageTitle="Customers / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Employees ===== */
      {
        path: '/admin/employees',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_employees"
          >
            <EmployeesPage pageTitle="Employees" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/employees/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_employees"
          >
            <EmployeeViewPage pageTitle="Employees / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Chefs & Waiters ===== */
      {
        path: '/admin/chefs',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_chefs"
          >
            <ChefsPage pageTitle="Chefs" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/chefs/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_chefs"
          >
            <ChefViewPage pageTitle="Chefs / View" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/waiters',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_waiters"
          >
            <WaitersPage pageTitle="Waiters" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/waiters/show/:id',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_waiters"
          >
            <WaiterViewPage pageTitle="Waiters / View" />
          </ProtectedRoute>
        ),
      },

      /* ===== Attendance & Leave ===== */
      {
        path: '/admin/attendance',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_attendance"
          >
            <AttendancePage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/leave-requests',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_leave"
          >
            <LeaveRequestsPage pageTitle="Leave Requests" />
          </ProtectedRoute>
        ),
      },

      /* ===== Transactions & Reports ===== */
      {
        path: '/admin/transactions',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_transactions"
          >
            <TransactionsPage pageTitle="Transactions" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/sales-report',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_sales_report"
          >
            <SalesReportsPage pageTitle="Sales Report" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/items-report',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_items_report"
          >
            <ItemsReportsPage pageTitle="Items Report" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/credit-balance-report',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_credit_balance_report"
          >
            <CreditBalanceReportsPage pageTitle="Credit Balance Report" />
          </ProtectedRoute>
        ),
      },

      /* ===== Profile ===== */
      {
        path: '/admin/profile/edit-profile',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_setting"
          >
            <EditProfilePage pageTitle="Edit Profile" />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/profile/change-password',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_setting"
          >
            <ChangePasswordPage pageTitle="Change Password" />
          </ProtectedRoute>
        ),
      },

      /* ===== Settings ===== */
      {
        path: '/admin/settings',
        element: <AdminSettingsLayout />,
        children: [
          {
            path: 'company',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <CompanyPage pageTitle="Company" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'license',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <LicenseSettingsPage pageTitle="License" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'site',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <SitePage pageTitle="Site" />
              </ProtectedRoute>
            ),
          },
          {
  path: 'branches',
  element: (
    <ProtectedRoute
      isAuthenticated={isAuthenticated}
      isSuperAdmin={isSuperAdmin}
      permissions={permissions}
      requiredPermission="update_setting"
    >
      <BranchesPage pageTitle="Branches" />
    </ProtectedRoute>
  ),
},

          {
            path: 'mail',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <MailPage pageTitle="Mail" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'pages',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <CMSPage pageTitle="Pages" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'analytics',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <AnalyticsPage pageTitle="Analytics" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'currencies',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <CurrenciesPage pageTitle="Currencies" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'item-attributes',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <ItemAttributesPage pageTitle="Item Attributes" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'item-categories',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <ItemCategoriesPage pageTitle="Item Categories" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'notification',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <NotificationPage pageTitle="Notification" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'notification-alert',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <NotificationAlertPage pageTitle="Notification Alert" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'otp',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <OTPPage pageTitle="OTP" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'shimbirr-auth',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <ShimbirrAuthSettingsPage pageTitle="Shimbirr Auth" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'payment-gateway',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <PaymentGatewayPage pageTitle="Payment Gateway" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'role',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <RolePermissionPage pageTitle="Role" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'role/:id',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <RoleUpdatePage pageTitle="Role" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'taxes',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <TaxPage pageTitle="Taxes" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'theme',
            element: (
              <ProtectedRoute
                isAuthenticated={isAuthenticated}
                isSuperAdmin={isSuperAdmin}
                permissions={permissions}
                requiredPermission="update_setting"
              >
                <ThemePage pageTitle="Theme" />
              </ProtectedRoute>
            ),
          },
                   {
           path: 'hrm',
           element: (
             <ProtectedRoute
               isAuthenticated={isAuthenticated}
               isSuperAdmin={isSuperAdmin}
               permissions={permissions}
               requiredPermission="update_setting"
             >
               <HRMSettingsPage pageTitle="HRM" />
             </ProtectedRoute>
           ),
         },
          // keep for later if you enable it
          // {
          //   path: 'sms-gateway',
          //   element: (
          //     <ProtectedRoute
          //       isAuthenticated={isAuthenticated}
          //       isSuperAdmin={isSuperAdmin}
          //       permissions={permissions}
          //       requiredPermission="update_setting"
          //     >
          //       <SmsGatewayPage pageTitle="SMS Gateway" />
          //     </ProtectedRoute>
          //   ),
          // },
        ],
      },

      /* ===== Inventory ===== */
      {
        path: '/admin/inventory',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_inventory"
          >
            <InventoryPage pageTitle="Inventory" />
          </ProtectedRoute>
        ),
      },

      /* ===== Kitchen & Waiter boards ===== */
      {
        path: '/admin/kitchen',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_kitchen_board"
          >
            <KitchenBoard />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/kitchen-screen',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_kitchen_screen"
          >
            <KitchenScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: '/admin/waiter-board',
        element: (
          <ProtectedRoute
            isAuthenticated={isAuthenticated}
            isSuperAdmin={isSuperAdmin}
            permissions={permissions}
            requiredPermission="view_waiter_board"
          >
            <WaiterBoard />
          </ProtectedRoute>
        ),
      },

      { path: '/admin/unauthorized', element: <UnauthorizedPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },

  // Customer area
  {
    path: '/customer',
    element: (
      <ProtectedRoute isAuthenticated={isAuthenticated} permissions={permissions}>
        <CustomerLayout />
      </ProtectedRoute>
    ),
    children: [
      { path: 'dashboard', element: <CustomerDashboard /> },
      { path: 'profile', element: <CustomerProfilePage /> },
      { path: 'credit', element: <CustomerCreditPage /> },
      { path: 'unpaid-credit', element: <CustomerUnpaidCreditPage /> },
      { path: 'orders', element: <CustomerOrdersPage /> },
      { path: 'order', element: <Navigate to="/menu/table/saabu8r" /> },
      { path: 'change-password', element: <CustomerChangePasswordPage pageTitle="Change Password" /> },
      { path: 'pay-credit', element: <CustomerPayCreditPage /> },
    ],
  },

  { path: '*', element: <NotFoundPage /> },
];

export default routes;
